import { useCallback } from "react";
import { useSetAtom } from "jotai";

import { MediaFragment } from "@src/fragments.generated";
import { AddMediaResources_TrainingResourceFragment } from "@src/features/app/dialogs/addMedia/Resources.generated";
import { addErrorToast } from "@src/features/app/utils/toast";
import { GraphqlOperations } from "@src/types.generated";
import { trainingResourceVersionAtom } from "../atoms";
import { useStartMediaToRichTextDocumentConversionMutation } from "../build/TrainingResourceBuildRightPane.generated";

/** A doc with no words in it yet — the only kind a PDF can be imported into. */
export const isRichTextDocumentBlank = (
  content: string | null | undefined,
): boolean => !content || content.replace(/<[^>]*>/g, "").trim() === "";

/**
 * Starts converting a PDF — freshly uploaded or an existing resource's — into
 * the blank Opus Doc being edited. The builder's side pane and the redesigned
 * page's canvas both offer it.
 */
const useImportPdfIntoDocument = (
  trainingResourceId: string | null | undefined,
): {
  handleImportPDF: (selectedMedia: MediaFragment) => void;
  handleImportResource: (
    resource: AddMediaResources_TrainingResourceFragment,
  ) => void;
} => {
  const setTrainingResourceVersion = useSetAtom(trainingResourceVersionAtom);
  const [startConversion] = useStartMediaToRichTextDocumentConversionMutation({
    refetchQueries: [
      GraphqlOperations.Query
        .TrainingResourceDetailPage_TrainingResourceVersion,
    ],
    awaitRefetchQueries: true,
    onCompleted(data) {
      if (
        !data.startTrainingResourceMediaToRichTextDocumentConversion.success
      ) {
        addErrorToast({
          data,
          callsite: "start_media_to_rich_text_document_conversion",
        });
      }
      const draftVersion =
        data.startTrainingResourceMediaToRichTextDocumentConversion
          .trainingResource?.draftVersion;
      setTrainingResourceVersion((val) =>
        val && draftVersion?.media
          ? { ...val, media: draftVersion.media }
          : val,
      );
    },
  });

  const importMedia = useCallback(
    (sourceMediaId: number | null | undefined) => {
      if (!trainingResourceId || !sourceMediaId) return;
      startConversion({
        variables: { input: { trainingResourceId, sourceMediaId } },
      });
    },
    [trainingResourceId, startConversion],
  );
  const handleImportPDF = useCallback(
    (selectedMedia: MediaFragment) => importMedia(selectedMedia?.id),
    [importMedia],
  );
  const handleImportResource = useCallback(
    (resource: AddMediaResources_TrainingResourceFragment) =>
      importMedia(resource.publishedVersion?.media?.id),
    [importMedia],
  );
  return { handleImportPDF, handleImportResource };
};

export default useImportPdfIntoDocument;
